import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetReferralStatisticsResponse,
  QueryReferralEventResponse,
  QueryReferredFriendActionsOptions,
  QueryReferredFriendActionsResponse,
  QueryReferringCustomerTotalsOptions,
  QueryReferringCustomerTotalsResponse,
  ReferralEvent,
  ReferralEventCreatedEnvelope,
  ReferralEventQuery,
  ReferralEventsQueryBuilder,
  getReferralEvent as universalGetReferralEvent,
  getReferralStatistics as universalGetReferralStatistics,
  queryReferralEvent as universalQueryReferralEvent,
  queryReferredFriendActions as universalQueryReferredFriendActions,
  queryReferringCustomerTotals as universalQueryReferringCustomerTotals,
  typedQueryReferralEvent as universalTypedQueryReferralEvent,
} from './loyalty-referral-v1-referral-event-tracker.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/referral' };

export function getReferralEvent(
  httpClient: HttpClient
): GetReferralEventSignature {
  return (referralEventId: string) =>
    universalGetReferralEvent(
      referralEventId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetReferralEventSignature {
  /**
   * Retrieves a referral event by ID.
   * @param - ID of the referral event to retrieve.
   * @returns Retrieved referral event.
   */
  (referralEventId: string): Promise<
    NonNullablePaths<
      ReferralEvent,
      | `referredFriendSignupEvent.referredFriendId`
      | `successfulReferralEvent.referredFriendId`
      | `successfulReferralEvent.referringCustomerId`
      | `actionEvent.referredFriendId`
      | `actionEvent.referringCustomerId`
      | `actionEvent.trigger.appId`
      | `actionEvent.trigger.activityType`
      | `rewardEvent.rewardedReferringCustomerId`
      | `rewardEvent.rewardedReferredFriendId`
      | `rewardEvent.referralRewardId`
      | `rewardEvent.rewardType`,
      4
    >
  >;
}

export function queryReferralEvent(
  httpClient: HttpClient
): QueryReferralEventSignature {
  return () =>
    universalQueryReferralEvent(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReferralEventSignature {
  /**
   * Creates a query to retrieve a list of referral events.
   *
   * The `queryReferralEvents()` function builds a query to retrieve a list of referral events and returns a `ReferralEventsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `ReferralEventsQueryBuilder` functions onto the query. `ReferralEventsQueryBuilder` functions enable you to sort, filter, and control the results `queryReferralEvents()` returns.
   *
   * `queryReferralEvents()` runs with these `ReferralEventsQueryBuilder` defaults, which you can override:
   *
   * - skip(0)
   * - limit(50)
   * - descending("_createdDate")
   *
   * The functions that are chained to `queryReferralEvents()` are applied in the order they're called. For example, if you apply ascending('actionEvent') and then descending('rewardEvent'), the results are sorted first by the action events, and then, if there are multiple results with the same action events, the items are sorted by reward events.
   */
  (): ReferralEventsQueryBuilder;
}

export function typedQueryReferralEvent(
  httpClient: HttpClient
): TypedQueryReferralEventSignature {
  return (query: ReferralEventQuery) =>
    universalTypedQueryReferralEvent(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryReferralEventSignature {
  /** */
  (query: ReferralEventQuery): Promise<
    NonNullablePaths<
      QueryReferralEventResponse,
      | `referralEvents`
      | `referralEvents.${number}.referredFriendSignupEvent.referredFriendId`
      | `referralEvents.${number}.successfulReferralEvent.referredFriendId`
      | `referralEvents.${number}.successfulReferralEvent.referringCustomerId`
      | `referralEvents.${number}.actionEvent.referredFriendId`
      | `referralEvents.${number}.actionEvent.referringCustomerId`
      | `referralEvents.${number}.actionEvent.trigger.appId`
      | `referralEvents.${number}.actionEvent.trigger.activityType`
      | `referralEvents.${number}.rewardEvent.rewardedReferringCustomerId`
      | `referralEvents.${number}.rewardEvent.rewardedReferredFriendId`
      | `referralEvents.${number}.rewardEvent.referralRewardId`
      | `referralEvents.${number}.rewardEvent.rewardType`,
      6
    >
  >;
}

export function getReferralStatistics(
  httpClient: HttpClient
): GetReferralStatisticsSignature {
  return () =>
    universalGetReferralStatistics(
      // @ts-ignore
      { httpClient }
    );
}

interface GetReferralStatisticsSignature {
  /**
   * Retrieves referral statistics.
   */
  (): Promise<
    NonNullablePaths<
      GetReferralStatisticsResponse,
      | `totalSignUpsCompleted`
      | `totalActionsCompleted`
      | `totalAmountGenerated`,
      2
    >
  >;
}

export function queryReferringCustomerTotals(
  httpClient: HttpClient
): QueryReferringCustomerTotalsSignature {
  return (options?: QueryReferringCustomerTotalsOptions) =>
    universalQueryReferringCustomerTotals(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReferringCustomerTotalsSignature {
  /**
   * Retrieves a list of referring customer totals, given the provided paging, filtering, and sorting.
   *
   * To learn about working with Query endpoints, see
   * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
   * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
   * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
   */
  (options?: QueryReferringCustomerTotalsOptions): Promise<
    NonNullablePaths<
      QueryReferringCustomerTotalsResponse,
      | `referringCustomerTotals`
      | `referringCustomerTotals.${number}.referringCustomerId`
      | `referringCustomerTotals.${number}.contactId`
      | `referringCustomerTotals.${number}.totalSuccessfulReferrals`
      | `referringCustomerTotals.${number}.totalAmountGenerated`
      | `referringCustomerTotals.${number}.totalFriendsWithActions`,
      4
    >
  >;
}

export function queryReferredFriendActions(
  httpClient: HttpClient
): QueryReferredFriendActionsSignature {
  return (options?: QueryReferredFriendActionsOptions) =>
    universalQueryReferredFriendActions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryReferredFriendActionsSignature {
  /**
   * Retrieves a list of referred friend actions, given the provided paging, filtering, and sorting.
   *
   * To learn about working with Query endpoints, see
   * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
   * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
   * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
   */
  (options?: QueryReferredFriendActionsOptions): Promise<
    NonNullablePaths<
      QueryReferredFriendActionsResponse,
      | `referredFriendActions`
      | `referredFriendActions.${number}.coupon._id`
      | `referredFriendActions.${number}.coupon.code`
      | `referredFriendActions.${number}.coupon.status`
      | `referredFriendActions.${number}.coupon.couponSpecification.minimumSubtotal`
      | `referredFriendActions.${number}.coupon.couponSpecification.name`
      | `referredFriendActions.${number}.coupon.couponSpecification.discountType`
      | `referredFriendActions.${number}.loyaltyPoints.transactionId`
      | `referredFriendActions.${number}.loyaltyPoints.amount`
      | `referredFriendActions.${number}.referredFriendId`
      | `referredFriendActions.${number}.contactId`
      | `referredFriendActions.${number}.trigger.appId`
      | `referredFriendActions.${number}.trigger.activityType`
      | `referredFriendActions.${number}.rewardType`
      | `referredFriendActions.${number}.totalActions`
      | `referredFriendActions.${number}.totalAmountSpent`,
      6
    >
  >;
}

export const onReferralEventCreated = EventDefinition(
  'wix.loyalty.referral.v1.referral_event_created',
  true,
  (event: ReferralEventCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ReferralEventCreatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  Coupon,
  CouponDiscountTypeOptionsOneOf,
  CouponScope,
  CouponScopeOrMinSubtotalOneOf,
  CreateReferralEventRequest,
  CreateReferralEventResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FixedAmountDiscount,
  GetReferralEventRequest,
  GetReferralEventResponse,
  GetReferralStatisticsRequest,
  GetReferralStatisticsResponse,
  Group,
  IdentificationData,
  IdentificationDataIdOneOf,
  LoyaltyPoints,
  MessageEnvelope,
  PercentageDiscount,
  QueryReferralEventRequest,
  QueryReferralEventResponse,
  QueryReferredFriendActionsOptions,
  QueryReferredFriendActionsRequest,
  QueryReferredFriendActionsResponse,
  QueryReferringCustomerTotalsOptions,
  QueryReferringCustomerTotalsRequest,
  QueryReferringCustomerTotalsResponse,
  ReferralEvent,
  ReferralEventCreatedEnvelope,
  ReferralEventEventTypeOneOf,
  ReferralEventQuerySpec,
  ReferralEventsQueryBuilder,
  ReferralEventsQueryResult,
  ReferredFriendAction,
  ReferredFriendActionEvent,
  ReferredFriendActionRewardTypeOptionsOneOf,
  ReferredFriendDetails,
  ReferredFriendSignupEvent,
  ReferringCustomerTotal,
  ReprocessReferralEventsRequest,
  ReprocessReferralEventsRequestEntityOneOf,
  ReprocessReferralEventsResponse,
  RestoreInfo,
  Reward,
  RewardEvent,
  RewardEventReceiverOneOf,
  SortOrder,
  Sorting,
  Status,
  SuccessfulReferralEvent,
  Trigger,
  V1ActionEvent,
  V1Coupon,
  V1SuccessfulReferralEvent,
  V1Trigger,
  WebhookIdentityType,
  utils,
} from './loyalty-referral-v1-referral-event-tracker.universal.js';
